import { AddressParser } from '../utils/addressParser';
import { getApiBaseUrl } from './getApiUrl';

export interface TempUserData {
  tempUuid: string;
  address?: string;
  projectType?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  formData?: any;
  createdAt?: string;
  updatedAt?: string;
}

export class TempUserService {
  // CB:BEGIN ensure-temp-session-id-helper
  private static generateTempSessionId(): string {
    return `temp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }

  private static getOrCreateTempSessionId(): string {
    try {
      let existing = localStorage.getItem('temp_session_id');
      if (!existing) {
        existing = TempUserService.generateTempSessionId();
        localStorage.setItem('temp_session_id', existing);
      }
      return existing;
    } catch {
      return TempUserService.generateTempSessionId();
    }
  }
  // CB:END ensure-temp-session-id-helper

  // CB:BEGIN ensure-temp-homeowner-endpoint
  static async ensureTempHomeowner(extra?: {
    address?: string;
    name?: string;
    phone?: string;
    sms_opt_in?: boolean;
    quick_estimate?: string;
  }): Promise<{ tempHomeownerId: string; tempSessionId: string; data: any }> {
    const tempSessionId = TempUserService.getOrCreateTempSessionId();
    const payload = {
      temp_session_id: tempSessionId,
      ...(extra || {}),
    };

    console.log('🔐 Ensuring temp homeowner record exists', {
      temp_session_id: tempSessionId,
      hasAddress: !!extra?.address,
    });

    const response = await fetch(`${getApiBaseUrl()}/api/temp-homeowner`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to ensure temp homeowner', { status: response.status, errorText });
      throw new Error(`Failed to ensure temp homeowner: ${response.status}`);
    }

    const data = await response.json();
    const tempHomeownerId = data?.id || data?.temp_homeowner_id || data?.tempHomeownerId;

    if (tempHomeownerId) {
      try {
        localStorage.setItem('temp_homeowner_id', tempHomeownerId);
      } catch {}
      console.log('✅ Temp homeowner ready', { tempHomeownerId, tempSessionId });
      return { tempHomeownerId, tempSessionId, data };
    }

    throw new Error('Temp homeowner response missing id');
  }
  // CB:END ensure-temp-homeowner-endpoint

  // Create temp user (general method)
  static async createTempUser(data: {
    address?: string;
    projectType?: string;
    formData?: any;
    fullName?: string;
    email?: string;
    phone?: string;
  }): Promise<{ tempUuid: string; address?: string; expiresAt: string }> {
    try {
      console.log('🔄 Creating temp user:', data);
      
      const response = await fetch(`${getApiBaseUrl()}/api/temp-user/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to create temp user: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Temp user created successfully:', result.tempUuid);
      
      // Store tempUuid in localStorage for persistence
      localStorage.setItem('tempUuid', result.tempUuid);
      
      return result;
    } catch (error) {
      console.error('❌ Error creating temp user:', error);
      throw error;
    }
  }

  // Create temp user with address (legacy method for backward compatibility)
  static async createTempUserWithAddress(address: string): Promise<{ tempUuid: string; address: string }> {
    try {
      console.log('🔄 Creating temp user with address:', address);
      
      const response = await fetch(`${getApiBaseUrl()}/api/temp-user/address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ address }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create temp user: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Temp user created successfully:', data.tempUuid);
      
      // Store tempUuid in localStorage for persistence
      localStorage.setItem('tempUuid', data.tempUuid);
      
      return data;
    } catch (error) {
      console.error('❌ Error creating temp user:', error);
      throw error;
    }
  }

  // Update temp user with project type
  static async updateTempUserProjectType(tempUuid: string, projectType: string): Promise<TempUserData> {
    try {
      console.log('🔄 Updating temp user project type:', { tempUuid, projectType });
      
      const response = await fetch(`${getApiBaseUrl()}/api/temp-user/${tempUuid}/project-type`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectType }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update temp user project type: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Temp user project type updated successfully');
      return data;
    } catch (error) {
      console.error('❌ Error updating temp user project type:', error);
      throw error;
    }
  }

  // Update temp user with form data
  static async updateTempUserFormData(tempUuid: string, formData: any): Promise<TempUserData> {
    try {
      console.log('🔄 Updating temp user form data:', { tempUuid, formDataKeys: Object.keys(formData) });
      
      const response = await fetch(`${getApiBaseUrl()}/api/temp-user/${tempUuid}/form-data`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ formData }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update temp user form data: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Temp user form data updated successfully');
      return data;
    } catch (error) {
      console.error('❌ Error updating temp user form data:', error);
      throw error;
    }
  }

  // Update temp user with personal details
  static async updateTempUserPersonalDetails(tempUuid: string, personalDetails: {
    fullName: string;
    email: string;
    phone: string;
  }): Promise<TempUserData> {
    try {
      console.log('🔄 Updating temp user personal details:', { tempUuid, ...personalDetails });
      
      const response = await fetch(`${getApiBaseUrl()}/api/temp-user/${tempUuid}/personal-details`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(personalDetails),
      });

      if (!response.ok) {
        throw new Error(`Failed to update temp user personal details: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Temp user personal details updated successfully');
      return data;
    } catch (error) {
      console.error('❌ Error updating temp user personal details:', error);
      throw error;
    }
  }

  // Update temp user (general method)
  static async updateTempUser(tempUuid: string, data: {
    address?: string;
    projectType?: string;
    formData?: any;
    fullName?: string;
    email?: string;
    phone?: string;
  }): Promise<TempUserData> {
    try {
      console.log('🔄 Updating temp user:', { tempUuid, updateFields: Object.keys(data) });
      
      const response = await fetch(`${getApiBaseUrl()}/api/temp-user/${tempUuid}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to update temp user: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Temp user updated successfully');
      return result;
    } catch (error) {
      console.error('❌ Error updating temp user:', error);
      throw error;
    }
  }

  // Delete temp user (for cancellation)
  static async deleteTempUser(tempUuid: string): Promise<void> {
    try {
      console.log('🗑️ Deleting temp user:', tempUuid);
      
      const response = await fetch(`${getApiBaseUrl()}/api/temp-user/${tempUuid}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete temp user: ${response.status}`);
      }

      console.log('✅ Temp user deleted successfully');
      
      // Clear tempUuid from localStorage after deletion
      localStorage.removeItem('tempUuid');
    } catch (error) {
      console.error('❌ Error deleting temp user:', error);
      throw error;
    }
  }

  // Merge temp user to permanent user after Clerk signup
  static async mergeTempToUser(tempUuid: string, userUuid: string, email: string): Promise<any> {
    try {
      console.log('🔄 Merging temp user to permanent user:', { tempUuid, userUuid, email });
      
      const response = await fetch(`${getApiBaseUrl()}/api/merge-temp-to-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tempUuid, userUuid, email }),
      });

      if (!response.ok) {
        throw new Error(`Failed to merge temp user: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Temp user merged to permanent user successfully');
      
      // Clear tempUuid from localStorage after successful merge
      localStorage.removeItem('tempUuid');
      
      return data;
    } catch (error) {
      console.error('❌ Error merging temp user:', error);
      throw error;
    }
  }

  // Get temp user by UUID
  static async getTempUser(tempUuid: string): Promise<TempUserData> {
    try {
      console.log('🔍 Getting temp user:', tempUuid);
      
      const response = await fetch(`${API_BASE_URL}/api/temp-user/${tempUuid}`);

      if (!response.ok) {
        throw new Error(`Failed to get temp user: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Temp user retrieved successfully');
      return data;
    } catch (error) {
      console.error('❌ Error getting temp user:', error);
      throw error;
    }
  }

  // Get tempUuid from localStorage; fallback to temp_session_id (estimate flow uses this key)
  static getTempUuid(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('tempUuid') || localStorage.getItem('temp_session_id');
  }

  // Set tempUuid in localStorage
  static setTempUuid(tempUuid: string): void {
    localStorage.setItem('tempUuid', tempUuid);
  }

  // Clear tempUuid from localStorage
  static clearTempUuid(): void {
    localStorage.removeItem('tempUuid');
  }

  // Check if user has a tempUuid
  static hasTempUuid(): boolean {
    return !!this.getTempUuid();
  }
} 